import React, { useRef, useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, ShoppingCart, Plus, Minus, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { gsap } from 'gsap';
import { getProductsInStock } from '../data/products';
import { useCart } from '../contexts/CartContext';
import { Product, ProductVariant } from '../types';
import ContactOptionsModal from '../components/ContactOptionsModal';
import { CART_ENABLED } from '../config/features';

// Общие функции для работы с вариантами товаров
const useProductVariant = (product: Product) => {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  // Автоматически выбираем первый доступный вариант при загрузке
  React.useEffect(() => {
    if (product.variants && product.variants.length > 0) {
      const availableVariant = product.variants.find(v => v.inStock);
      if (availableVariant) {
        setSelectedVariant(availableVariant);
      } else {
        // Если нет доступных вариантов, выбираем первый для отображения
        setSelectedVariant(product.variants[0]);
      }
    }
  }, [product.variants]);

  const handleVariantChange = (variant: ProductVariant) => {
    setSelectedVariant(variant);
  };

  const getCurrentPrice = () => selectedVariant?.price || product.price;

  const getCurrentWeight = () => selectedVariant?.weight || (product.variants && product.variants[0] ? product.variants[0].weight : '');

  const getCurrentImage = () => selectedVariant?.image || product.image;

  return {
    selectedVariant,
    handleVariantChange,
    getCurrentPrice,
    getCurrentWeight,
    getCurrentImage
  };
};

// Мемоизированный компонент для мобильной карточки товара
const MobileProductCard = memo(({ 
  product, 
  getCurrentQuantity, 
  isProductInCart, 
  decrementQuantity, 
  incrementQuantity, 
  handleQuantityChange, 
  handleAddToCart, 
  formatPrice 
}: {
  product: Product;
  getCurrentQuantity: (id: string, variantId?: string) => number;
  isProductInCart: (id: string, variantId?: string) => boolean;
  decrementQuantity: (id: string, variantId?: string) => void;
  incrementQuantity: (id: string, variantId?: string) => void;
  handleQuantityChange: (id: string, variantId?: string, value?: number) => void;
  handleAddToCart: (id: string, variantId?: string) => void;
  formatPrice: (price: number) => string;
}) => {
  const { selectedVariant, handleVariantChange, getCurrentPrice, getCurrentWeight, getCurrentImage } = useProductVariant(product);

  // Определяем текущую цену
  const currentPrice = getCurrentPrice();

  return (
    <div className="card group overflow-hidden">
      {/* Изображение */}
      <div className="relative overflow-hidden shop-image-container-scaled">
        <Link to={`/product/${product.id}`} state={{ from: 'shop' }}>
          <img
            src={getCurrentImage()}
            alt={product.name}
            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer shop-image-scaled"
            loading="lazy"
          />
        </Link>
        {!product.inStock && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
            Под заказ
          </div>
        )}
        

      </div>

      {/* Контент */}
      <div className="p-4">
        <Link to={`/product/${product.id}`} state={{ from: 'shop' }}>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 h-10 flex items-center group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200 cursor-pointer">
            {product.name}
          </h3>
        </Link>
        
        {/* Выбор веса (если есть варианты) */}
        {product.variants && product.variants.length > 0 && (
          <div className="hidden md:block mb-4">
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
              <button
                onClick={() => {
                  if (!product.variants) return;
                  const currentIndex = product.variants.findIndex(v => v.id === selectedVariant?.id);
                  let prevIndex = currentIndex - 1;
                  
                  // Ищем предыдущий доступный вариант
                  while (prevIndex >= 0 && !product.variants[prevIndex].inStock) {
                    prevIndex--;
                  }
                  
                  // Если не нашли доступный вариант, ищем с конца
                  if (prevIndex < 0) {
                    prevIndex = product.variants.length - 1;
                    while (prevIndex >= 0 && !product.variants[prevIndex].inStock) {
                      prevIndex--;
                    }
                  }
                  
                  if (prevIndex >= 0) {
                    handleVariantChange(product.variants[prevIndex]);
                  }
                }}
                className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-3">
                {getCurrentWeight()}
              </span>
              
              <button
                onClick={() => {
                  if (!product.variants) return;
                  const currentIndex = product.variants.findIndex(v => v.id === selectedVariant?.id);
                  let nextIndex = currentIndex + 1;
                  
                  // Ищем следующий доступный вариант
                  while (nextIndex < product.variants.length && !product.variants[nextIndex].inStock) {
                    nextIndex++;
                  }
                  
                  // Если не нашли доступный вариант, ищем с начала
                  if (nextIndex >= product.variants.length) {
                    nextIndex = 0;
                    while (nextIndex < product.variants.length && !product.variants[nextIndex].inStock) {
                      nextIndex++;
                    }
                  }
                  
                  if (nextIndex < product.variants.length) {
                    handleVariantChange(product.variants[nextIndex]);
                  }
                }}
                className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Счетчик количества */}
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => decrementQuantity(product.id, selectedVariant?.id)}
                className="w-6 h-6 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              >
                <Minus className="w-3 h-3" />
              </button>
              
              <input
                type="number"
                min="1"
                value={getCurrentQuantity(product.id, selectedVariant?.id)}
                onChange={(e) => handleQuantityChange(product.id, selectedVariant?.id, parseInt(e.target.value))}
                className="w-10 text-center bg-transparent border-none text-sm font-semibold text-gray-900 dark:text-white focus:outline-none"
              />
              
              <button
                onClick={() => incrementQuantity(product.id, selectedVariant?.id)}
                className="w-6 h-6 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            
            <span className="text-sm text-gray-500 dark:text-gray-400">шт.</span>
          </div>
        </div>

        {/* Цена и корзина */}
        <div className="flex items-center justify-between">
          {currentPrice && (
            <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
              {formatPrice(currentPrice)} ₽
            </span>
          )}
          
          <button
            onClick={() => handleAddToCart(product.id, selectedVariant?.id)}
            disabled={selectedVariant ? !selectedVariant.inStock : false}
            className={`flex-1 ml-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed ${
              isProductInCart(product.id, selectedVariant?.id)
                ? 'bg-green-100 dark:bg-green-900 hover:bg-green-200 dark:hover:bg-primary-800'
                : 'bg-primary-100 dark:bg-primary-900 hover:bg-primary-200 dark:hover:bg-primary-800'
            }`}
          >
            {CART_ENABLED || (selectedVariant && !selectedVariant.inStock) ? (
              <ShoppingCart className={`w-4 h-4 ${
                isProductInCart(product.id, selectedVariant?.id)
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-primary-600 dark:text-primary-400'
              }`} />
            ) : (
              <Phone className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            )}
            <span className={`text-xs font-medium ${
              isProductInCart(product.id, selectedVariant?.id)
                ? 'text-green-600 dark:text-green-400'
                : 'text-primary-600 dark:text-primary-400'
            }`}>
              {selectedVariant && !selectedVariant.inStock 
                ? 'Под заказ' 
                : !CART_ENABLED
                  ? 'Связаться'
                : isProductInCart(product.id, selectedVariant?.id) 
                  ? 'В корзине' 
                  : 'Купить'
              }
            </span>
          </button>
        </div>
      </div>
    </div>
  );
});

MobileProductCard.displayName = 'MobileProductCard';

// Мемоизированный компонент для десктопной карточки товара
const DesktopProductCard = memo(({ 
  product, 
  getCurrentQuantity, 
  isProductInCart, 
  decrementQuantity, 
  incrementQuantity, 
  handleQuantityChange, 
  handleAddToCart, 
  formatPrice 
}: {
  product: Product;
  getCurrentQuantity: (id: string, variantId?: string) => number;
  isProductInCart: (id: string, variantId?: string) => boolean;
  decrementQuantity: (id: string, variantId?: string) => void;
  incrementQuantity: (id: string, variantId?: string) => void;
  handleQuantityChange: (id: string, variantId?: string, value?: number) => void;
  handleAddToCart: (id: string, variantId?: string) => void;
  formatPrice: (price: number) => string;
}) => {
  const { selectedVariant, handleVariantChange, getCurrentPrice, getCurrentWeight, getCurrentImage } = useProductVariant(product);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);



  // Определяем текущую цену
  const currentPrice = getCurrentPrice();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 dark:border-gray-700">
      <div className="flex flex-col lg:flex-row">
        {/* Изображение товара */}
        <div className="lg:w-1/3 relative group cursor-pointer overflow-hidden shop-image-container-scaled">
          <Link to={`/product/${product.id}`} state={{ from: 'shop' }}>
            <img
              src={getCurrentImage()}
              alt={product.name}
              className="w-full h-64 lg:h-full object-cover transition-transform duration-500 group-hover:scale-110 shop-image-scaled"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-lg font-semibold">
                Подробнее
              </div>
            </div>
          </Link>
        </div>

        {/* Информация о товаре */}
        <div className="lg:w-2/3 p-8 flex flex-col justify-between">
          <div>
            <Link to={`/product/${product.id}`} state={{ from: 'shop' }}>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-300 cursor-pointer">
                {product.name}
              </h2>
            </Link>
            
            {/* Описание с возможностью сворачивания */}
            <div className="mb-6">
              <div className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                <p className={isDescriptionExpanded ? '' : 'line-clamp-3'}>
                  {product.fullDescription}
                </p>
              </div>
              
              {/* Кнопка сворачивания/разворачивания */}
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="mt-3 text-primary-600 dark:text-primary-400 text-sm font-medium hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200 flex items-center space-x-1"
              >
                <span>{isDescriptionExpanded ? 'Свернуть' : 'Читать далее'}</span>
                {isDescriptionExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>
            
            <div className="flex items-center space-x-4 mb-6">
              <span className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                {currentPrice ? formatPrice(currentPrice) : '0'} ₽
              </span>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-sm font-medium rounded-full">
                В наличии
              </span>
            </div>
          </div>

          {/* Выбор веса (если есть варианты) */}
          {product.variants && product.variants.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <button
                  onClick={() => {
                    if (!product.variants) return;
                    const currentIndex = product.variants.findIndex(v => v.id === selectedVariant?.id);
                    let prevIndex = currentIndex - 1;
                    
                    // Ищем предыдущий доступный вариант
                    while (prevIndex >= 0 && !product.variants[prevIndex].inStock) {
                      prevIndex--;
                    }
                    
                    // Если не нашли доступный вариант, ищем с конца
                    if (prevIndex < 0) {
                      prevIndex = product.variants.length - 1;
                      while (prevIndex >= 0 && !product.variants[prevIndex].inStock) {
                        prevIndex--;
                      }
                    }
                    
                    if (prevIndex >= 0) {
                      handleVariantChange(product.variants[prevIndex]);
                    }
                  }}
                  className="w-10 h-10 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <span className="text-base font-medium text-gray-700 dark:text-gray-300 px-4">
                  {getCurrentWeight()}
                </span>
                
                <button
                  onClick={() => {
                    if (!product.variants) return;
                    const currentIndex = product.variants.findIndex(v => v.id === selectedVariant?.id);
                    let nextIndex = currentIndex + 1;
                    
                    // Ищем следующий доступный вариант
                    while (nextIndex < product.variants.length && !product.variants[nextIndex].inStock) {
                      nextIndex++;
                    }
                    
                    // Если не нашли доступный вариант, ищем с начала
                    if (nextIndex >= product.variants.length) {
                      nextIndex = 0;
                      while (nextIndex < product.variants.length && !product.variants[nextIndex].inStock) {
                        nextIndex++;
                      }
                    }
                    
                    if (nextIndex < product.variants.length) {
                      handleVariantChange(product.variants[nextIndex]);
                    }
                  }}
                  className="w-10 h-10 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Функционал заказа */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
            {/* Поле количества */}
            <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
              <button
                onClick={() => decrementQuantity(product.id, selectedVariant?.id)}
                className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              >
                <Minus className="w-4 h-4" />
              </button>
              
              <input
                type="number"
                min="1"
                value={getCurrentQuantity(product.id, selectedVariant?.id)}
                onChange={(e) => handleQuantityChange(product.id, selectedVariant?.id, parseInt(e.target.value))}
                className="w-16 text-center bg-transparent border-none text-lg font-semibold text-gray-900 dark:text-white focus:outline-none"
              />
              
              <button
                onClick={() => incrementQuantity(product.id, selectedVariant?.id)}
                className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Кнопка добавления в корзину */}
            <button
              id={`add-btn-${product.id}`}
              onClick={() => handleAddToCart(product.id, selectedVariant?.id)}
              disabled={selectedVariant ? !selectedVariant.inStock : false}
              className={`flex items-center justify-center space-x-2 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg w-[260px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                isProductInCart(product.id, selectedVariant?.id)
                  ? 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white'
                  : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white'
              }`}
            >
              {CART_ENABLED || (selectedVariant && !selectedVariant.inStock) ? (
                <ShoppingCart className="w-5 h-5" />
              ) : (
                <Phone className="w-5 h-5" />
              )}
              <span>
                {selectedVariant && !selectedVariant.inStock
                  ? 'Под заказ'
                  : !CART_ENABLED
                    ? 'Связаться'
                  : isProductInCart(product.id, selectedVariant?.id) 
                    ? 'В корзину' 
                    : 'Добавить в корзину'
                }
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

DesktopProductCard.displayName = 'DesktopProductCard';

const Shop: React.FC = () => {
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const desktopContainerRef = useRef<HTMLDivElement>(null);
  const { cart, setCartItem, setIsOpen, updateQuantity } = useCart();
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const products = useMemo(() => getProductsInStock(), []);

  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  }, []);



  useEffect(() => {
    // Инициализируем количество для каждого товара
    const initialQuantities = products.reduce((acc, product) => {
      acc[product.id] = 1;
      return acc;
    }, {} as { [key: string]: number });
    setQuantities(initialQuantities);

    // GSAP контекст для правильной очистки
    const ctx = gsap.context(() => {
      // Анимация для мобильного контейнера
      if (mobileContainerRef.current) {
        const mobileElements = mobileContainerRef.current.children;
        gsap.set(mobileElements, { willChange: 'transform, opacity' });
        gsap.fromTo(mobileElements,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            onComplete: () => { 
              gsap.set(mobileElements, { willChange: 'auto' }); 
            }
          }
        );
      }

      // Анимация для десктопного контейнера
      if (desktopContainerRef.current) {
        const desktopElements = desktopContainerRef.current.children;
        gsap.set(desktopElements, { willChange: 'transform, opacity' });
        gsap.fromTo(desktopElements,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            onComplete: () => { 
              gsap.set(desktopElements, { willChange: 'auto' }); 
            }
          }
        );
      }
    });

    // Cleanup функция
    return () => {
      ctx.revert();
    };
  }, [products]);



  // Проверка, находится ли товар в корзине (с учетом варианта)
  const isProductInCart = useCallback((productId: string, variantId?: string) => {
    return cart.items.some(item => 
      item.product.id === productId && item.variantId === variantId
    );
  }, [cart.items]);

  const handleQuantityChange = useCallback((productId: string, variantId: string | undefined, value?: number) => {
    // Проверяем, доступен ли товар
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    // Если есть вариант, проверяем его доступность
    if (variantId) {
      const variant = product.variants?.find(v => v.id === variantId);
      if (!variant || !variant.inStock) return;
    }

    if (!CART_ENABLED) {
      setIsContactModalOpen(true);
      const button = document.getElementById(`add-btn-${productId}`);
      if (button) {
        gsap.set(button, { willChange: 'transform' });
        gsap.to(button, {
          scale: 1.06,
          duration: 0.18,
          yoyo: true,
          repeat: 1,
          onComplete: () => {
            gsap.set(button, { willChange: 'auto' });
          }
        });
      }
      return;
    }
    
    const newValue = Math.max(1, value || 1);
    
    if (isProductInCart(productId, variantId)) {
      // Если товар в корзине, обновляем количество в корзине
      updateQuantity(productId, variantId, newValue);
    } else {
      // Если товара нет в корзине, обновляем локальное состояние
      const key = variantId ? `${productId}-${variantId}` : productId;
      setQuantities(prev => ({ ...prev, [key]: newValue }));
    }
  }, [isProductInCart, updateQuantity, products]);

  // Получение текущего количества товара (из корзины или локального состояния)
  const getCurrentQuantity = useCallback((productId: string, variantId?: string) => {
    const cartItem = cart.items.find(item => 
      item.product.id === productId && item.variantId === variantId
    );
    const key = variantId ? `${productId}-${variantId}` : productId;
    return cartItem ? cartItem.quantity : (quantities[key] || 1);
  }, [cart.items, quantities]);

  // Обработка клика по кнопке корзины
  const handleCartButtonClick = useCallback((productId: string, variantId?: string) => {
    // Проверяем, доступен ли товар
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    // Если есть вариант, проверяем его доступность
    if (variantId) {
      const variant = product.variants?.find(v => v.id === variantId);
      if (!variant || !variant.inStock) return;
    }
    
    const cartItem = cart.items.find(item => 
      item.product.id === productId && item.variantId === variantId
    );
    
    if (cartItem) {
      // Если товар уже в корзине, открываем корзину
      setIsOpen(true);
    } else {
      // Если товара нет в корзине, добавляем его с точным количеством
      const quantity = getCurrentQuantity(productId, variantId);
      if (product) {
        setCartItem(product, variantId, quantity);
      }
    }
    
    // Оптимизированная анимация кнопки с ref вместо getElementById
    const button = document.getElementById(`add-btn-${productId}`);
    if (button) {
      gsap.set(button, { willChange: 'transform' });
      gsap.to(button, {
        scale: 1.1,
        duration: 0.2,
        yoyo: true,
        repeat: 1,
        onComplete: () => { 
          gsap.set(button, { willChange: 'auto' }); 
        }
      });
    }
  }, [cart.items, setIsOpen, getCurrentQuantity, products, setCartItem]);

  const handleAddToCart = useCallback((productId: string, variantId?: string) => {
    handleCartButtonClick(productId, variantId);
  }, [handleCartButtonClick]);

  const incrementQuantity = useCallback((productId: string, variantId?: string) => {
    // Проверяем, доступен ли товар
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    // Если есть вариант, проверяем его доступность
    if (variantId) {
      const variant = product.variants?.find(v => v.id === variantId);
      if (!variant || !variant.inStock) return;
    }
    
    const currentQuantity = getCurrentQuantity(productId, variantId);
    handleQuantityChange(productId, variantId, currentQuantity + 1);
  }, [getCurrentQuantity, handleQuantityChange, products]);

  const decrementQuantity = useCallback((productId: string, variantId?: string) => {
    // Проверяем, доступен ли товар
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    // Если есть вариант, проверяем его доступность
    if (variantId) {
      const variant = product.variants?.find(v => v.id === variantId);
      if (!variant || !variant.inStock) return;
    }
    
    const currentQuantity = getCurrentQuantity(productId, variantId);
    handleQuantityChange(productId, variantId, currentQuantity - 1);
  }, [getCurrentQuantity, handleQuantityChange, products]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Заголовок */}
        <div className="text-center mb-16">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6 transition-all duration-300 hover:scale-105 group"
          >
            <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
            <span>Вернуться на главную</span>
          </Link>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Товары в наличии
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Качественные экологически чистые продукты для ваших нужд
          </p>
        </div>

        {/* Сообщение, если товаров нет в наличии */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 dark:border-gray-700 max-w-3xl mx-auto">
              <div className="mb-6">
                <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Товары временно недоступны
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                На данный момент нет возможности покупки нашей продукции на сайте. Для приобретения свяжитесь с нами любым удобным для вас способом.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Товары для мобильных - 2 колонки */}
            <div ref={mobileContainerRef} className="md:hidden grid grid-cols-2 gap-4 mb-12">
              {products.map((product) => (
                <MobileProductCard
                  key={product.id}
                  product={product}
                  getCurrentQuantity={getCurrentQuantity}
                  isProductInCart={isProductInCart}
                  decrementQuantity={decrementQuantity}
                  incrementQuantity={incrementQuantity}
                  handleQuantityChange={handleQuantityChange}
                  handleAddToCart={handleAddToCart}
                  formatPrice={formatPrice}
                />
              ))}
            </div>

            {/* Товары для десктопа - оригинальный дизайн */}
            <div ref={desktopContainerRef} className="hidden md:block space-y-8">
              {products.map((product) => (
                <DesktopProductCard
                  key={product.id}
                  product={product}
                  getCurrentQuantity={getCurrentQuantity}
                  isProductInCart={isProductInCart}
                  decrementQuantity={decrementQuantity}
                  incrementQuantity={incrementQuantity}
                  handleQuantityChange={handleQuantityChange}
                  handleAddToCart={handleAddToCart}
                  formatPrice={formatPrice}
                />
              ))}
            </div>
          </>
        )}

        {/* Информационный блок */}
        <div className="mt-20 text-center">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Почему выбирают нас?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Все наши продукты производятся с использованием экологически чистых технологий, 
              проходят строгий контроль качества и соответствуют международным стандартам.
            </p>
          </div>
        </div>
      </div>
      <ContactOptionsModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        subtitle="Покупка на сайте временно отключена. Напишите или позвоните нам, чтобы уточнить наличие и условия приобретения."
      />
    </div>
  );
};

export default Shop;
